/* eslint-disable @next/next/no-img-element */
import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import { useRecoilState } from "recoil";
import clsx from "clsx";
import Tippy from "@tippyjs/react";
import { AnimatePresence } from "framer-motion";

import "tippy.js/dist/tippy.css";
import styles from "../../styles/tinymce.module.css";

import { engageState } from "../../atoms/platform";
import { apiGetAgenda } from "../../lib/api/engage";
import {
  getGroupForTitle,
  getGroupForTitleEngage,
  getItemNumber,
  getItemNumberOnly,
  tarrantInlineText,
} from "../../helpers/text";
// Tarrant agendaGroup id 682aa91b3c8f84005c5b9f81
import { calculateDuration } from "../../helpers/timing";
import { textToHtml, textToHtmlTinyMCE } from "../../helpers/converter";
import {
  getSubheadingByGroupId,
  getNameByGroupId,
  getParentByGroupId,
} from "../../helpers/arrays";
import { getLocalStorage } from "../../helpers/storage";

import { DownloadIcon } from "@heroicons/react/outline";
import { MdOndemandVideo } from "react-icons/md";
import { FaRegComment } from "react-icons/fa";
import { IoChevronBack } from "react-icons/io5";
import { HiOutlineDocumentDuplicate } from "react-icons/hi";

// import AgendaTopicInfo from "./AgendaTopicInfo";
import AgendaTopicFull from "./AgendaTopicFull";
import AgendaTopicInfoMobile from "./AgendaTopicInfoMobile";
// import DetailsMobile from "./UpDetailsMobile";
import Loading from "../Shared/Loading";
import EngageHeader from "../Builders/Header/EngageHeader";
import Supplemental from "./Supplemental";
import SupplementalMobile from "./SupplementalMobile";
import UserWayWidget from "../UserWayWidget";

const AgendaPreview = () => {
  const router = useRouter();
  const { client, agenda } = router.query;
  const [showDetails, setShowDetails] = useState(false);
  const [mobileDetails, setMobileDetails] = useState(false);
  const [topicDetails, setTopicDetails] = useState();
  const [data, setData] = useState();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [supplemental, setSupplemental] = useState({
    visible: false,
    meeting: null,
  });
  const [mobileSupplemental, setMobileSupplemental] = useState({
    visible: false,
    meeting: null,
  });
  const [platform, setPlatform] = useRecoilState(engageState);

  useEffect(() => {
    async function fetchAgenda() {
      setLoading(true);
      const result = await apiGetAgenda(agenda);
      if (result) {
        const tempPlatform = getLocalStorage("platform");
        setPlatform(tempPlatform);
        setData(result);
        setLoading(false);
      } else {
        setError(true);
      }
    }
    if (agenda) {
      fetchAgenda();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agenda]);

  function handleChosenTopic(topic) {
    setTopicDetails(topic);
    setShowDetails(true);
  }

  function handleMobileTopic(topic) {
    setTopicDetails(topic);
    setMobileDetails(true);
  }

  function handleRegister(topic) {
    setTopicDetails(topic);
  }

  return (
    <div
      className="antialiased bg-white max-w-4xl mx-auto pb-72"
      style={{ width: "9.5in", minHeight: "11in" }}
    >
      {data && (
        <div className={clsx("bg-white")}>
          {data?.coverOptions?.useHeader && (
            <EngageHeader
              header={data?.pdfAgenda.headerDetails}
              agendaFont={data?.department.agendaFont}
            />
          )}

          <div
            className={clsx(
              data?.coverOptions?.useHeader && "border-t border-gray-400",
              "pb-2 grid grid-cols-4 mt-2",
            )}
          >
            {/* Date Infod */}
            {data?.coverOptions?.useDateLocation && (
              <div
                className={clsx(
                  "col-span-2 pt-1.5 pb-2",
                  data?.department.agendaFont === "arial" && "font-arial",
                  data?.department.agendaFont === "sans-serif" && "font-sans",
                  data?.department.agendaFont === "timesNewRoman" &&
                    "font-timesNewRoman",
                  data?.department.agendaFont === "noto" && "font-noto",
                )}
              >
                <p className="text-xs 2xl:text-sm text-black font-semibold">
                  {data?.scheduleDate}
                </p>
                <p className="text-xs 2xl:text-sm text-black">
                  {data?.room.name}
                </p>
                <p className="text-xs 2xl:text-sm text-black">
                  {data?.room?.address?.street}, {data?.room?.address?.city},
                  {data?.room?.address?.state} {data?.room.address?.zipCode}
                </p>
                {platform?.showDuration && (
                  <p className="text-xs 2xl:text-sm text-black italic">
                    Est. Duration: {calculateDuration(data?.actionTopics)}
                  </p>
                )}
              </div>
            )}

            {/* Supplemental Materials */}
            <div className="mt-4 lg:mt-0 flex justify-end items-end space-x-2 col-span-4 2xl:col-span-1">
              <div className="flex items-center space-x-2 mr-2">
                {platform?.allowSupplemental &&
                  data?.attachments?.length > 0 && (
                    <Tippy
                      content="Supplemental Materials"
                      placement="bottom"
                      animation="scale"
                      delay={[500, 0]}
                    >
                      <div
                        className="flex px-2 py-0.5 md:px-4 md:py-1 rounded-md items-center justify-evenly bg-blue-600 text-blueGray-200 hover:bg-blue-400 transform duration-200 focus:outline-none"
                        onClick={() =>
                          setSupplemental({
                            visible: true,
                            meeting: data,
                          })
                        }
                      >
                        <HiOutlineDocumentDuplicate className="h-6 w-6 xl:h-8 xl:w-8 text-blueGray-200 cursor-pointer p-0 md:p-1" />
                      </div>
                    </Tippy>
                  )}

                {data?.videoUrl && (
                  <Tippy
                    content="Watch Video of the Meeting"
                    placement="bottom"
                    animation="scale"
                    delay={[500, 0]}
                  >
                    <div>
                      <a
                        href={`${process.env.NEXT_PUBLIC_BASE_URL}/player/${client}/${data?.id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="flex px-2 py-0.5 md:px-4 md:py-1 rounded-md items-center justify-evenly bg-blue-600 text-blueGray-200 hover:bg-blue-400 transform duration-200 focus:outline-none"
                      >
                        <MdOndemandVideo className="h-6 w-6 xl:h-8 xl:w-8 text-blueGray-200 cursor-pointer p-0 md:p-1" />
                      </a>
                    </div>
                  </Tippy>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile header */}
      {data && (
        <div className="sticky md:hidden top-0 px-4 sm:px-6 pt-2 bg-white">
          <div className="pb-1 border-b border-gray-400">
            <div className="relative flex items-center justify-center">
              <h3 className="text-sm lg:text-lg xl:text-2xl leading-6 font-medium text-gray-900">
                {data?.department?.name}
              </h3>
              <IoChevronBack
                className="absolute top-1 left-0 h-6 w-6 text-gray-800 cursor-pointer hover:text-gray-400"
                aria-hidden="true"
                onClick={() => router.back()}
              />
            </div>
            {data?.coverOptions?.useDateLocation && (
              <div
                className={clsx(
                  "flex flex-col items-center",
                  data?.department.agendaFont === "arial" && "font-arial",
                  data?.department.agendaFont === "sans-serif" && "font-sans",
                  data?.department.agendaFont === "timesNewRoman" &&
                    "font-timesNewRoman",
                  data?.department.agendaFont === "noto" && "font-noto",
                )}
              >
                <p className="text-xs lg:text-lg text-gray-700 italic">
                  {data?.scheduleDate}
                </p>
                {platform?.showDuration && (
                  <p className="text-xs lg:text-lg text-gray-700 italic">
                    Est. Duration: {calculateDuration(data?.actionTopics)}
                  </p>
                )}

                <p className="text-xs lg:text-lg text-gray-700">
                  {data?.room.name}
                </p>
                <p className="text-xs lg:text-lg text-gray-700">
                  {data?.room?.address?.street}, {data?.room?.address?.city},
                  {data?.room?.address?.state} {data?.room?.address?.zipCode}
                </p>
              </div>
            )}

            <div className="mt-2 flex justify-end items-center space-x-2">
              {platform?.allowSupplemental && data?.attachments?.length > 0 && (
                <Tippy
                  content="Supplemental Resources"
                  placement="bottom"
                  animation="scale"
                  delay={[500, 0]}
                >
                  <div>
                    <div
                      className="flex px-2 py-0.5 rounded-md items-center justify-evenly text-black focus:outline-none"
                      onClick={() => {
                        setMobileSupplemental({
                          visible: true,
                          meeting: data,
                        });
                      }}
                    >
                      <HiOutlineDocumentDuplicate className="h-5 w-5" />
                    </div>
                  </div>
                </Tippy>
              )}

              {data?.videoUrl && (
                <Tippy
                  content="Watch Video of the Meeting"
                  placement="bottom"
                  animation="scale"
                  delay={[500, 0]}
                >
                  <div>
                    <a
                      href={`${process.env.NEXT_PUBLIC_BASE_URL}/player/${client}/${data?.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex px-2 py-0.5 rounded-md items-center justify-evenly text-black focus:outline-none"
                    >
                      <MdOndemandVideo className="h-5 w-5" />
                    </a>
                  </div>
                </Tippy>
              )}
              {data?.agendaPdfUrl && data?.publishStatus !== "draftreview" && (
                <Tippy
                  content="Download Agenda"
                  placement="bottom"
                  animation="scale"
                  delay={[500, 0]}
                >
                  <div>
                    <a
                      href={data?.agendaPdfUrl}
                      className="flex px-2 py-0.5 rounded-md items-center justify-evenly text-black focus:outline-none"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <DownloadIcon className="h-5 w-5" />
                    </a>
                  </div>
                </Tippy>
              )}
              {data?.packetPdfUrl && data?.publishStatus !== "draftreview" && (
                <Tippy
                  content="Download Agenda Packet"
                  placement="bottom"
                  animation="scale"
                  delay={[500, 0]}
                >
                  <div>
                    <a
                      href={data?.packetPdfUrl}
                      className="flex px-2 py-0.5 rounded-md items-center justify-evenly text-black focus:outline-none"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <DownloadIcon className="h-5 w-5 mr-1" />
                      Packet
                    </a>
                  </div>
                </Tippy>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Agenda Details */}
      {data && (
        <div className="">
          {/* Start Agenda */}
          <div className="mt-4 hidden lg:block">
            {/* Is Revised */}
            {data?.isRevised && (
              <div className="mt-0.5 w-full text-center">
                <p className="text-red-600 font-semibold text-sm xl:text-base 2xl:text-lg">
                  ** Revised **
                </p>
              </div>
            )}
            <div className={`mb-4 ${styles.tinyContainer}`}>
              {textToHtmlTinyMCE(data?.pdfAgenda.agendaHeadingTitle)}
              {textToHtmlTinyMCE(data?.pdfAgenda.agendaHeadingBlurb)}
            </div>
            {/* List */}
            <div
              className={clsx(
                data?.department.agendaFont === "arial" && "font-arial",
                data?.department.agendaFont === "sans-serif" && "font-sans",
                data?.department.agendaFont === "timesNewRoman" &&
                  "font-timesNewRoman",
                data?.department.agendaFont === "noto" && "font-noto",
              )}
            >
              {data &&
                data?.groupOrder.map((group, index) => {
                  const subheading = getSubheadingByGroupId(
                    group.id,
                    data?.groupSubheadings,
                  );
                  const parentGroup = getParentByGroupId(
                    group.id,
                    data?.groupSubheadings,
                  );
                  const name = getNameByGroupId(
                    group.id,
                    data?.groupSubheadings,
                    group.name,
                  );
                  const groupActions = data?.actionTopics
                    .map((topic) => {
                      if (
                        topic.agendaGroup.id === group.id &&
                        !topic.bulkTopic &&
                        !topic.registrationTopic
                      ) {
                        return topic;
                      }
                      return null;
                    })
                    .filter((topic) => topic !== null);
                  groupActions.sort((a, b) => a.order - b.order);
                  return (
                    <>
                      {group?.id.toString() === "682aa91b3c8f84005c5b9f81" && (
                        <div className={`mb-4 ${styles.tinyContainer}`}>
                          {textToHtmlTinyMCE(tarrantInlineText)}
                        </div>
                      )}
                      <div
                        key={`group-${index}`}
                        className={clsx(
                          group?.parentGroup && "ml-8",
                          "mb-4 xl:mb-8 text-black",
                        )}
                      >
                        <div
                          className={clsx(
                            data?.coverOptions?.useSeperators &&
                              "border-b border-black",
                          )}
                        >
                          <p
                            className={clsx(
                              data?.department.agendaOptions.agendaGroupBold &&
                                "font-semibold",
                              data?.department.agendaOptions
                                ?.agendaGroupFontSize &&
                                `text-${data.department.agendaOptions.agendaGroupFontSize}`,
                            )}
                          >
                            {getGroupForTitleEngage(
                              data?.groupNumbering,
                              index,
                              data?.groupOrder,
                              data?.childNumbering,
                            )}{" "}
                            {name}
                          </p>
                        </div>
                        <p
                          className={clsx(
                            data?.department.agendaOptions
                              .groupSubheadingItalic && "italic",
                            data?.department.agendaOptions
                              ?.groupSubheadingSize &&
                              `text-${data.department.agendaOptions.groupSubheadingSize}`,
                            "text-black pt-1 ml-2 whitespace-pre-line text-justify",
                          )}
                        >
                          {subheading}
                        </p>
                        {/* {groupActions.length === 0 && (
                        <p className="ml-4 xl:ml-8 font-light italic pt-2">
                          No Items
                        </p>
                      )} */}
                        {groupActions.map((topic, i) => {
                          return (
                            <div key={topic.id}>
                              <div
                                className={clsx(
                                  i === 0 ? "mt-4" : "",
                                  "ml-4 xl:ml-8 mb-2 select-none",
                                )}
                              >
                                <div className="flex">
                                  {/* Number - stays in place */}
                                  <span
                                    className={clsx(
                                      "text-sm text-black flex-shrink-0",
                                      data?.department.agendaOptions
                                        .titleBold && "font-semibold",
                                      data?.department.agendaOptions
                                        ?.titleFontSize &&
                                        `md:text-${data.department.agendaOptions.titleFontSize}`,
                                    )}
                                  >
                                    {getGroupForTitle(
                                      data?.groupNumbering,
                                      index,
                                      data?.groupOrder,
                                      data?.childNumbering,
                                    )}
                                    {getItemNumberOnly(
                                      data.actionTopics,
                                      data.itemNumbering,
                                      topic,
                                      i,
                                      data?.groupNumbering,
                                      data?.childNumbering,
                                    )}
                                  </span>
                                  <p
                                    className={clsx(
                                      topic?.hasDetails || topic?.attachments
                                        ? "cursor-pointer text-blue-700 hover:text-blue-800"
                                        : "text-black ",
                                      data?.department.agendaOptions
                                        .titleBold && "font-semibold",
                                      data?.department.agendaOptions
                                        .titleUnderline &&
                                        "underline underline-offset-2",
                                      data?.department.agendaOptions
                                        ?.titleFontSize &&
                                        `md:text-${data.department.agendaOptions.titleFontSize}`,
                                      data?.department.agendaOptions.titleIndent
                                        ? "ml-12"
                                        : "ml-4",
                                      data?.department.agendaOptions
                                        ?.fullJustify
                                        ? "text-justify"
                                        : "text-left",
                                    )}
                                    onClick={() => {
                                      if (
                                        !topic.bulkTopic &&
                                        (topic.hasDetails || topic.attachments)
                                      ) {
                                        handleChosenTopic(topic);
                                      }
                                    }}
                                  >
                                    {topic.title}
                                  </p>

                                  {!topic.templateTopic &&
                                    topic.allowRegister &&
                                    platform?.embedSignUp && (
                                      <div className="ml-2 cursor-pointer text-blue-600 hover:text-blue-400 transform duration-200">
                                        <FaRegComment className="h-5 w-5" />
                                      </div>
                                    )}
                                </div>
                                {topic.votedByBoard && (
                                  <p
                                    className={clsx(
                                      data?.department.agendaOptions
                                        ?.detailsFontSize &&
                                        `text-${data.department.agendaOptions.detailsFontSize}`,
                                      data?.department.agendaOptions.titleIndent
                                        ? "ml-20"
                                        : "ml-8",
                                      data?.department.agendaOptions
                                        ?.fullJustify
                                        ? "text-justify"
                                        : "text-left",
                                      "whitespace-pre-line",
                                    )}
                                  >
                                    &#40;{topic.votedByBoard?.name}&#41;
                                  </p>
                                )}
                                {topic.topicBlurb.length > 0 && (
                                  <span
                                    className={clsx(
                                      data?.department.agendaOptions
                                        ?.detailsFontSize &&
                                        `text-${data.department.agendaOptions.detailsFontSize}`,
                                      data?.department.agendaOptions.titleIndent
                                        ? "ml-20"
                                        : "ml-8",
                                      data?.department.agendaOptions
                                        ?.fullJustify
                                        ? "text-justify"
                                        : "text-left",
                                      "whitespace-pre-line inline-block",
                                    )}
                                  >
                                    {topic.topicBlurb}
                                  </span>
                                )}

                                {client !== "broomfield" &&
                                  topic.subTopics?.length > 0 &&
                                  topic.subTopics.map((sub, sIdx) => {
                                    if (client === "tarrantcountytx") {
                                      const letter = String.fromCharCode(
                                        97 + sIdx,
                                      ); // a, b, c, ...
                                      return (
                                        <p
                                          key={`sub-${sIdx}`}
                                          className={clsx(
                                            data?.department.agendaOptions
                                              ?.detailsFontSize &&
                                              `text-${data.department.agendaOptions.detailsFontSize}`,
                                            data?.department.agendaOptions
                                              .titleIndent
                                              ? "ml-20"
                                              : "ml-8",
                                            data?.department.agendaOptions
                                              ?.fullJustify
                                              ? "text-justify"
                                              : "text-left",
                                            "py-0.5",
                                          )}
                                        >
                                          {letter}&#41; {sub}
                                        </p>
                                      );
                                    } else {
                                      return (
                                        <p
                                          key={`sub-${sIdx}`}
                                          className={clsx(
                                            data?.department.agendaOptions
                                              ?.detailsFontSize &&
                                              `text-${data.department.agendaOptions.detailsFontSize}`,
                                            data?.department.agendaOptions
                                              .titleIndent
                                              ? "ml-20"
                                              : "ml-8",
                                            data?.department.agendaOptions
                                              ?.fullJustify
                                              ? "text-justify"
                                              : "text-left",
                                            "py-0.5",
                                          )}
                                        >
                                          {![
                                            "santafespringsca",
                                            "coloradocountytx",
                                          ].includes(client) && (
                                            <>&#8212; </>
                                          )}{" "}
                                          {sub}
                                        </p>
                                      );
                                    }
                                  })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  );
                })}
            </div>
            <div className={`mt-4 ${styles.tinyContainer}`}>
              {textToHtmlTinyMCE(data?.pdfAgenda.agendaFooterBlurb)}
            </div>
            {platform && (
              <div className="mt-6 flex w-full justify-between">
                <div>
                  {platform.useSignImage ? (
                    <>
                      <img
                        src={platform.signatureImage}
                        alt="signature"
                        className="h-auto w-52"
                      />
                    </>
                  ) : (
                    <>
                      {data?.signature && (
                        <>
                          <img
                            src={data?.signature}
                            alt="signature"
                            className="h-auto w-52"
                          />
                        </>
                      )}
                    </>
                  )}
                  <div className={`${styles.tinyContainer}`}>
                    {textToHtmlTinyMCE(data?.pdfAgenda.agendaFooterNote)}
                  </div>
                </div>
              </div>
            )}
            {platform?.useSeal && (
              <div className="flex justify-end">
                <img
                  src={platform.signatureSeal}
                  alt="signature"
                  className="h-32 w-32 rounded-full"
                />
              </div>
            )}
            <p className="text-xs 2xl:text-lg text-gray-700 italic">
              Date Posted: {data?.publishedDate}
            </p>
          </div>

          {/* Mobile Data */}
          <div className="px-2 sm:px-4 lg:hidden">
            {/* Is Revised */}
            {data?.isRevised && (
              <div className="mt-0.5 w-full text-center">
                <p className="text-red-600 font-semibold text-mobile sm:text-sm">
                  ** Revised **
                </p>
              </div>
            )}
            <div className="mt-2 mb-3">
              {textToHtml(data?.pdfAgenda.agendaHeadingTitle)}
              {textToHtml(data?.pdfAgenda.agendaHeadingBlurb)}
            </div>
            <div className="w-full flex flex-col space-y-2">
              {data?.groupOrder.map((group, index) => {
                const subheading = getSubheadingByGroupId(
                  group.id,
                  data?.groupSubheadings,
                );
                const name = getNameByGroupId(
                  group.id,
                  data?.groupSubheadings,
                  group.name,
                );
                const groupActions = data?.actionTopics
                  .map((topic) => {
                    if (topic.agendaGroup.id === group.id) {
                      if (!topic.bulkTopic) {
                        return topic;
                      } else {
                        return null;
                      }
                    }
                    return null;
                  })
                  .filter((topic) => topic !== null);
                groupActions.sort((a, b) => a.order - b.order);
                return (
                  <>
                    {group?.id.toString() === "682aa91b3c8f84005c5b9f81" && (
                      <div className={`mb-4`}>
                        {textToHtml(tarrantInlineText)}
                      </div>
                    )}
                    <div
                      key={index}
                      className={clsx(
                        data?.department.agendaFont === "arial" && "font-arial",
                        data?.department.agendaFont === "sans-serif" &&
                          "font-sans",
                        data?.department.agendaFont === "timesNewRoman" &&
                          "font-timesNewRoman",
                        data?.department.agendaFont === "noto" && "font-noto",
                      )}
                    >
                      <div className="border-b border-gray-800 mb-2">
                        <p className="text-black text-mobile sm:text-sm font-medium">
                          {getGroupForTitleEngage(
                            data?.groupNumbering,
                            index,
                            data?.groupOrder,
                            data?.childNumbering,
                          )}
                          {name}
                        </p>
                      </div>
                      <p className="text-mobile text-black pt-1 ml-2 mb-2 whitespace-pre-line text-justify font-medium">
                        {subheading}
                      </p>
                      {/* {groupActions.length === 0 && (
                      <p className="text-mobile text-black font-light italic ml-4">
                        No Items
                      </p>
                    )} */}
                      {groupActions.map((topic, i) => (
                        <div
                          key={topic.id}
                          className={clsx(
                            i === 0 ? "mt-4" : "",
                            (topic?.hasDetails || topic?.attachments) &&
                              "cursor-pointer hover:text-blue-700",
                            "ml-4 mb-2 text-black select-none font-medium",
                          )}
                          onClick={() => {
                            if (
                              !topic.bulkTopic &&
                              (topic.hasDetails || topic.attachments)
                            ) {
                              handleMobileTopic(topic);
                            }
                          }}
                        >
                          <p className="ml-2 text-mobile font-medium whitespace-pre-line">
                            {getGroupForTitle(
                              data?.groupNumbering,
                              index,
                              data?.groupOrder,
                              data?.childNumbering,
                            )}
                            {getItemNumber(
                              data.actionTopics,
                              data.itemNumbering,
                              topic,
                              i,
                              data?.groupNumbering,
                              data?.childNumbering,
                            )}
                          </p>
                          <p className="ml-2 text-mobile whitespace-pre-line font-medium">
                            {topic.topicBlurb}
                          </p>
                          {client !== "broomfield" &&
                            topic.subTopics?.length > 0 &&
                            topic.subTopics.map((sub, sIdx) => {
                              return (
                                <p
                                  key={`sub-${sIdx}`}
                                  className="ml-2 py-0.5 text-mobile font-medium"
                                >
                                  {![
                                    "santafespringsca",
                                    "coloradocountytx",
                                  ].includes(client) && <>&#8212; </>}{" "}
                                  {sub}
                                </p>
                              );
                            })}
                        </div>
                      ))}
                    </div>
                  </>
                );
              })}
              <div className="mt-4">
                {textToHtml(data?.pdfAgenda.agendaFooterBlurb)}
              </div>
              {platform && (
                <div className="mt-6 flex w-full justify-between">
                  {platform.useSignImage ? (
                    <>
                      <img
                        src={platform.signatureImage}
                        alt="signature"
                        className="h-auto w-40"
                      />
                    </>
                  ) : (
                    <>
                      {data?.signature && (
                        <>
                          <img
                            src={data?.signature}
                            alt="signature"
                            className="h-auto w-40"
                          />
                        </>
                      )}
                    </>
                  )}
                  {textToHtml(data?.pdfAgenda.agendaFooterNote)}
                </div>
              )}
              {platform?.useSeal && (
                <div>
                  <img
                    src={platform.signatureSeal}
                    alt="signature"
                    className="h-16 w-16 rounded-full"
                  />
                </div>
              )}
            </div>
            <p className="text-xs 2xl:text-lg text-gray-700 italic mt-2">
              Date Posted: {data?.publishedDate}
            </p>
          </div>
          {/* End Content */}
        </div>
      )}

      {showDetails && (
        <AgendaTopicFull
          visible={setShowDetails}
          topic={topicDetails.id}
          enablePri={platform?.enablePriorities}
          client={client}
        />
      )}
      {supplemental?.visible && (
        <Supplemental
          meeting={supplemental?.meeting}
          update={setSupplemental}
        />
      )}
      <AnimatePresence>
        {mobileDetails && (
          <AgendaTopicInfoMobile
            visible={setMobileDetails}
            topic={topicDetails.id}
            enablePri={platform?.enablePriorities}
            client={client}
            register={handleRegister}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {mobileSupplemental?.visible && (
          <SupplementalMobile
            meeting={mobileSupplemental?.meeting}
            update={setMobileSupplemental}
          />
        )}
      </AnimatePresence>

      {loading && <Loading />}
      {error && (
        <div className="fixed z-10 inset-0 overflow-hidden">
          <div className="relative flex flex-col min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <img
              src="/images/jmCircle.svg"
              className="h-12 2xl:h-32 w-auto absolute top-4 left-4 z-50"
              alt="justMeetings logo"
            />
            <div className="fixed inset-0 bg-coolGray-900 transition-opacity"></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">
              &#8203;
            </span>
            {/* Start Modal */}
            <div className="inline-block bg-coolGray-900 rounded-lg text-left transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:px-6 sm:pt-2 sm:pb-8">
              <div className="grid grid-cols-1 gap-2 my-10">
                <div>
                  <div className="mt-3 text-center">
                    <h3
                      className="text-lg 2xl:text-4xl leading-6 font-bold text-blueGray-200"
                      id="modal-title"
                    >
                      Invalid Meeting
                    </h3>
                    <div className="mt-2 xl:mt-8">
                      <p className="text-lg text-blueGray-300 mb-2 text-center font-medium">
                        Your url is using{" "}
                        <span className="font-bold text-white">{agenda}</span>,
                        which is not a valid meeting within JustMeetings.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* End Modal */}
          </div>
        </div>
      )}
      <UserWayWidget />
    </div>
  );
};

export default AgendaPreview;
